// Building the org-roam index from a sync backend - org-roam-db-sync, adapted.
//
// org-roam walks a directory on disk and reads every file. organice reaches its
// files over a network backend where a listing is a request and every file is
// another one, so the same walk is the expensive thing on this page. Three
// consequences shape what follows:
//
//   - Depth is bounded (MAX_DEPTH). A misconfigured roam directory pointed at
//     the root of a large drive should cost a bounded number of requests, not an
//     unbounded recursion.
//   - Files are fetched a few at a time (BATCH_SIZE) rather than all at once. A
//     thousand parallel requests is how you get rate-limited by GitLab or time
//     out a WebDAV server.
//   - Failures are collected, not thrown. One unreadable file must not lose the
//     999 that read fine, and the page shows what it could not read.
//
// The full scan is `syncRoamDatabase`; `syncRoamFile` re-indexes one file, which
// is what "and then when changing something" needs - the same operation
// org-roam performs on save, without rescanning the world.
// `reindexFileFromText` is that same operation for a file organice is holding
// in memory, which is the only version that works before a push.

import { emptyDatabase, mergeFile, parseRoamFile, removeFile } from './org_roam';
import { orgFileExtensions } from './org_utils';

// Deep enough for any real notes directory, shallow enough that a mistake is
// cheap. org-roam itself has no limit; it is also not making HTTP requests.
export const MAX_DEPTH = 6;

// Concurrent file reads. Small on purpose - see the header comment.
export const BATCH_SIZE = 4;

export const isOrgPath = (path) => orgFileExtensions.test(`${path || ''}`);

const listingEntries = (listing) =>
  ((listing && listing.listing) || []).map((entry) => ({
    path: entry.get ? entry.get('path') : entry.path,
    name: entry.get ? entry.get('name') : entry.name,
    isDirectory: entry.get ? entry.get('isDirectory') : entry.isDirectory,
  }));

/**
 * Every .org file at or below `directory`, breadth-first and depth-bounded.
 *
 * A directory that cannot be listed is recorded and skipped rather than failing
 * the scan: a permissions problem in one subfolder is not a reason to have no
 * index at all.
 */
export const collectOrgFiles = async (
  client,
  directory,
  { maxDepth = MAX_DEPTH, onProgress } = {}
) => {
  const files = [];
  const errors = [];
  const seen = new Set();

  let frontier = [`${directory || '/'}`];
  let depth = 0;

  while (frontier.length && depth <= maxDepth) {
    const next = [];

    // Directories one level at a time, so progress is reportable and the
    // request rate stays bounded by the width of the tree rather than its size.
    for (let index = 0; index < frontier.length; index += BATCH_SIZE) {
      const slice = frontier.slice(index, index + BATCH_SIZE);

      const listings = await Promise.all(
        slice.map((path) =>
          client
            .getDirectoryListing(path)
            .then((listing) => ({ path, listing }))
            .catch((error) => ({ path, error }))
        )
      );

      listings.forEach(({ path, listing, error }) => {
        if (error) {
          errors.push({ path, message: `Could not list: ${error.message || error}` });
          return;
        }

        listingEntries(listing).forEach((entry) => {
          if (!entry.path || seen.has(entry.path)) return;
          seen.add(entry.path);

          if (entry.isDirectory) next.push(entry.path);
          else if (isOrgPath(entry.path)) files.push(entry.path);
        });
      });

      if (onProgress) onProgress({ phase: 'listing', files: files.length, depth });
    }

    frontier = next;
    depth += 1;
  }

  if (frontier.length) {
    errors.push({
      path: directory,
      message: `Stopped at ${maxDepth} directory levels — deeper folders were not indexed.`,
    });
  }

  return { files: files.sort(), errors };
};

/**
 * Scan a whole directory into a fresh index (org-roam-db-sync).
 *
 * Fresh rather than merged into what is already stored: a full sync is also how
 * a file that has been deleted or renamed stops being in the index, and merging
 * would keep the ghosts forever.
 */
export const syncRoamDatabase = async (
  client,
  { directory, onProgress, now = () => Date.now() } = {}
) => {
  if (!client) return { database: null, errors: [{ path: directory, message: 'Not connected.' }] };

  const { files, errors } = await collectOrgFiles(client, directory, { onProgress });

  let database = { nodes: [], links: [], files: [], syncedAt: null };
  let done = 0;

  for (let index = 0; index < files.length; index += BATCH_SIZE) {
    const slice = files.slice(index, index + BATCH_SIZE);

    const contents = await Promise.all(
      slice.map((path) =>
        client
          .getFileContents(path)
          .then((text) => ({ path, text }))
          .catch((error) => ({ path, error }))
      )
    );

    contents.forEach(({ path, text, error }) => {
      if (error) {
        errors.push({ path, message: `Could not read: ${error.message || error}` });
        return;
      }

      database = mergeFile(database, parseRoamFile(path, text));
    });

    done += slice.length;
    if (onProgress) onProgress({ phase: 'reading', done, total: files.length });
  }

  return { database: { ...database, syncedAt: now() }, errors };
};

/**
 * Re-index one file in an existing database - the incremental case.
 *
 * A file that has disappeared is removed from the index rather than left as a
 * stale set of nodes pointing at nothing.
 */
export const syncRoamFile = async (client, database, path, { now = () => Date.now() } = {}) => {
  if (!client) return { database, errors: [{ path, message: 'Not connected.' }] };

  try {
    const contents = await client.getFileContents(path);
    return {
      database: { ...mergeFile(database, parseRoamFile(path, contents)), syncedAt: now() },
      errors: [],
    };
  } catch (error) {
    return {
      database: { ...removeFile(database, path), syncedAt: now() },
      errors: [{ path, message: `Could not read, so it was dropped from the index.` }],
    };
  }
};

/**
 * Re-index one file from text organice already holds, with no request at all.
 *
 * `syncRoamFile` above re-reads the file from the backend, which is exactly
 * wrong for an edit that has not been pushed yet: it would index the bytes as
 * they were *before* the edit and quietly undo it in the index. Handing the text
 * in instead is what lets a headline that became a node a moment ago be found a
 * moment later - by the node picker, by an `id:` link, by the finder - rather
 * than after a push and a manual full sync.
 *
 * `syncedAt` is deliberately carried through untouched. It records when the
 * whole directory was last scanned, which this is not, and the roam page shows
 * it to the user.
 *
 * Parsed with the default todo keywords, exactly as the full scan does. Passing
 * the file's real keyword sets here would make a node's title depend on which
 * path happened to index it; both being equally approximate is the lesser evil.
 */
export const reindexFileFromText = (database, path, contents) =>
  mergeFile(database || emptyDatabase(), parseRoamFile(path, contents));
